/**
 * Is this destination the page being shown? - the one-row bar, 29 September 2026.
 *
 * `NavLink` answers from the path alone, so `/contact?about=support` and
 * `/contact?about=feedback` were **both** "current" on either page. With
 * Support in the bar and Feedback inside the account menu that would light the
 * bar link and the menu button at once, so this also compares the query: every
 * parameter the destination names must match. Extra parameters on the page do
 * not matter.
 *
 * `end` means the path must match exactly (the dashboard at `/`); otherwise a
 * page below the destination counts (`/orders/123` is under Orders).
 */
export interface Destination {
  to: string;
  end?: boolean;
}

export function isCurrent(
  destination: Destination,
  location: { pathname: string; search: string },
): boolean {
  const [path = '', query = ''] = destination.to.split('?');

  const pathMatches =
    destination.end === true
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(`${path.replace(/\/$/, '')}/`);
  if (!pathMatches) return false;

  const wanted = new URLSearchParams(query);
  const present = new URLSearchParams(location.search);
  for (const [key, value] of wanted) {
    if (present.get(key) !== value) return false;
  }
  return true;
}

/** True when any of `destinations` is current: a menu that holds the page. */
export function holdsCurrent(
  destinations: readonly Destination[],
  location: { pathname: string; search: string },
): boolean {
  return destinations.some((destination) => isCurrent(destination, location));
}
